import React, { useState } from 'react';
import { Download, Loader, RotateCcw } from 'lucide-react';
import { MARK } from '../../../constants';
import { useMarcarAlterado } from './AlteracaoDoWorkflow';

/**
 * "Importar da etapa anterior": traz o que falta, sem mexer no que existe.
 *
 * Cada etapa lê a anterior UMA vez, quando é aberta pela primeira vez e ainda
 * não tem nada gravado. Depois disso o caminho de volta some: uma FIV salva
 * com duas doadoras nunca mais olhava a OPU, mesmo que a OPU tivesse dez.
 * O dado estava lá o tempo todo — a FIV é que deixou de perguntar.
 *
 * Este botão refaz a pergunta. É manual de propósito: rodar sozinho ao abrir
 * a aba ressuscitaria a linha que a equipe apagou de propósito, e apagar de
 * propósito é uma decisão tão legítima quanto esquecer de puxar.
 *
 * Importar aqui é só acrescentar. A linha que já existe não é substituída,
 * e é por isso que o botão pode ser clicado sem medo no meio do lançamento.
 *
 * `onImportar` devolve quantas linhas entraram. Zero não é falha — é "olhei,
 * não faltava nada", e precisa ser dito, senão o clique não responde nada e
 * parece quebrado.
 *
 * Pode devolver `{ trazidas, atualizadas }` no lugar do número, para a etapa
 * que também CORRIGE o que já está na lista — a TE faz isso com a numeração
 * dos embriões. Dizer "nada faltando" depois de mexer em seis números era o
 * pior dos dois mundos: a tela mudava e a mensagem jurava que não.
 *
 * SUBSTITUIR (`onSubstituir`, opcional) é o outro botão: descarta o que está
 * na etapa e puxa tudo da anterior de novo. Importar não resolve quando a
 * anterior MUDOU — a linha que existe aqui não é tocada, e "nada faltando" é a
 * resposta. Substituir é a resposta para "refazer esta etapa a partir da
 * outra".
 *
 * Ele devolve:
 *   - um número: quantas linhas ficaram no lugar;
 *   - `{ bloqueio: 'por quê' }`: não pode agora (ex.: embrião já transferido);
 *   - `null`: a pessoa desistiu na confirmação.
 * A confirmação, com o que vai ser perdido, é da aba — só ela sabe o que pesa.
 * Nada é gravado aqui: a troca fica na tela até o "Salvar Workflow", e fechar
 * sem salvar desfaz. Por isso o workflow é marcado como alterado.
 */
const ImportarDaEtapa = ({
  rotulo, fonte, singular, plural, onImportar, className = '',
  onSubstituir = null, rotuloSubstituir = null,
}) => {
  const marcarAlterado = useMarcarAlterado();
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const substituir = async () => {
    setImportando(true);
    setResultado(null);
    try {
      const devolvido = await onSubstituir();
      if (devolvido === null || devolvido === undefined) return;
      if (devolvido.bloqueio) {
        setResultado({ erro: devolvido.bloqueio });
        return;
      }
      marcarAlterado();
      setResultado({ substituidas: Number(devolvido) || 0 });
    } catch (e) {
      console.error(`${rotuloSubstituir}:`, e);
      setResultado({ erro: e.message || 'não deu para substituir agora' });
    } finally {
      setImportando(false);
    }
  };

  const importar = async () => {
    setImportando(true);
    setResultado(null);
    try {
      const devolvido = await onImportar();
      const mudou = typeof devolvido === 'number'
        ? devolvido > 0
        : (devolvido?.trazidas || 0) + (devolvido?.atualizadas || 0) > 0;
      if (mudou) marcarAlterado();
      setResultado(typeof devolvido === 'number'
        ? { trazidas: devolvido, atualizadas: 0 }
        : { trazidas: 0, atualizadas: 0, ...devolvido });
    } catch (e) {
      console.error(`${rotulo}:`, e);
      setResultado({ erro: e.message || 'não deu para importar agora' });
    } finally {
      setImportando(false);
    }
  };

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={importar}
        disabled={importando}
        className="flex items-center gap-1 text-xs text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded transition-colors disabled:opacity-50"
        title={`Importa ${plural.split(' ')[0]} ${fonte} que ainda não estão aqui. Não altera o que já foi preenchido.`}
      >
        {importando
          ? <Loader className="w-3.5 h-3.5 animate-spin" />
          : <Download className="w-3.5 h-3.5" />}
        {importando ? 'Importando...' : rotulo}
      </button>

      {onSubstituir && (
        <button
          type="button"
          onClick={substituir}
          disabled={importando}
          className="flex items-center gap-1 text-xs text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded transition-colors disabled:opacity-50"
          title={`Descarta o que está nesta etapa e puxa tudo ${fonte} de novo. Só grava ao salvar.`}
        >
          <RotateCcw className="w-3.5 h-3.5" />
          {rotuloSubstituir || `Substituir ${fonte}`}
        </button>
      )}

      {resultado && !importando && (
        <span className="text-xs text-slate-600">
          {resultado.erro ? (
            <><span aria-hidden="true">{MARK.WARN}</span> {resultado.erro}</>
          ) : resultado.substituidas !== undefined ? (
            <>
              <span aria-hidden="true">{MARK.OK}</span>{' '}
              {resultado.substituidas} {resultado.substituidas === 1 ? singular : plural} {fonte},
              {' '}no lugar do que havia. Salve para gravar.
            </>
          ) : resultado.trazidas === 0 && resultado.atualizadas === 0 ? (
            'Nada faltando.'
          ) : (
            <>
              <span aria-hidden="true">{MARK.OK}</span>{' '}
              {[
                resultado.trazidas > 0
                  && `${resultado.trazidas} ${resultado.trazidas === 1 ? singular : plural} ${fonte}`,
                resultado.atualizadas > 0
                  && `${resultado.atualizadas} ${resultado.atualizadas === 1 ? 'número corrigido' : 'números corrigidos'}`,
              ].filter(Boolean).join(', ')}.
            </>
          )}
        </span>
      )}
    </div>
  );
};

export default ImportarDaEtapa;
